import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { t as translate, RTL_LANGS } from "@/i18n/translations";
import { useAuth } from "@/context/AuthContext";

const I18nContext = createContext(null);
export const useI18n = () => useContext(I18nContext);

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLangState] = useState(localStorage.getItem("cml_lang") || "en");
  const [languages, setLanguages] = useState([]);
  const guessed = useRef(false);  // the browser is only asked once
  const [recentLangs, setRecentLangs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("cml_recent_langs") || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    api.get("/languages").then(({ data }) => setLanguages(data)).catch(() => {});
  }, []);

  // once per visit, tell MiLu which languages this browser reads — that is how demand is spotted
  useEffect(() => {
    if (sessionStorage.getItem("cml_langs_asked")) return;
    sessionStorage.setItem("cml_langs_asked", "1");
    const browser = navigator.languages?.length ? [...navigator.languages]
                                                : [navigator.language || "en"];
    api.post("/langs/asked", { browser: browser.slice(0, 6), picked: lang }).catch(() => {});
  }, [lang]);

  useEffect(() => {
    if (user && user.language && !localStorage.getItem("cml_lang")) setLangState(user.language);
  }, [user]);

  // a first-time visitor is met in the language their browser asks for, once
  useEffect(() => {
    if (guessed.current || localStorage.getItem("cml_lang") || user || languages.length === 0) return;
    guessed.current = true;
    const known = new Map(languages.map((l) => [l.code.toLowerCase(), l.code]));
    const asked = (navigator.languages?.length ? navigator.languages : [navigator.language || ""])
      .flatMap((tag) => [tag.toLowerCase(), tag.toLowerCase().split("-")[0]]);
    const found = asked.map((code) => known.get(code)).find(Boolean);
    if (found) setLangState(found);
  }, [languages, user]);

  const dir = RTL_LANGS.includes(lang) ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  const setLang = async (code) => {
    if (code !== lang) {
      const next = [lang, ...recentLangs.filter((c) => c !== lang && c !== code)].slice(0, 3);
      setRecentLangs(next);
      localStorage.setItem("cml_recent_langs", JSON.stringify(next));
    }
    setLangState(code);
    localStorage.setItem("cml_lang", code);
    if (user) {
      try {
        await api.put("/me/preferences", { language: code });
      } catch (err) {
        console.error("Could not save the language preference:", err);
      }
    }
  };

  const value = useMemo(
    () => ({ lang, setLang, dir, languages, recentLangs, t: (p) => translate(lang, p) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, dir, languages, recentLangs, user]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
